// The heavy widget-tier actions load dynamically to stay out of the bootstrap
// bundle; the prompt builder is static because its own chunk cost more in
// round-trips than the ~60 gzipped bytes it saved.

import { buildButtonPromptSelected } from '@/actions/messages/buildButtonPromptSelected';
import type { MessagePayload } from '@/actions/messages/sendMessage';
import type { ActionEnvironment } from '@/domain/actionEnvironment';
import type { ButtonPromptSelectedPayload } from '@/domain/message';
import { DisplayMode } from '@/domain/ui';
import { coolBeaconConnection, warmBeaconConnection } from '@/lib/beacon/beaconConnection';
import { logger } from '@/lib/logger';

export const OPEN_WIDGET_AND_SEND_SOURCES = { BUTTON_PROMPTS: 'BUTTON_PROMPTS' } as const;

export type OpenWidgetAndSendSource = (typeof OPEN_WIDGET_AND_SEND_SOURCES)[keyof typeof OPEN_WIDGET_AND_SEND_SOURCES];

// What a resolved send means. The bridge gives up on several paths that reject
// nothing, and a caller that speaks to the shopper — telling them chat is on its
// way — has to know which of those it is holding.
//
// TIMED_OUT says the caller was released, not that the prompt failed: the send is
// never cancelled and may still land.
export const OPEN_WIDGET_AND_SEND_OUTCOMES = {
  SENT: 'SENT',
  DROPPED: 'DROPPED',
  ABSORBED: 'ABSORBED',
  STALE: 'STALE',
  TIMED_OUT: 'TIMED_OUT',
} as const;

export type OpenWidgetAndSendOutcome =
  (typeof OPEN_WIDGET_AND_SEND_OUTCOMES)[keyof typeof OPEN_WIDGET_AND_SEND_OUTCOMES];

export interface OpenWidgetAndSendContext extends ButtonPromptSelectedPayload {
  source: OpenWidgetAndSendSource;
}

export interface OpenWidgetAndSendDeps {
  getActionEnv: () => ActionEnvironment | null;
  isWidgetLoaded: () => boolean;
  loadFullWidget: (options: { initialMessage?: string }) => Promise<void>;
  // Reports an open the click drives before the widget exists — only the
  // bootstrap tier knows the entry point the shopper left — and answers whether
  // the open is on record, so the reveal below does not report it a second time.
  trackWidgetOpened: () => boolean;
}

export interface OpenWidgetAndSendOptions {
  // Checked once the widget is up, before anything is sent. A launcher torn down
  // mid-load cannot be unwound by its own teardown, so the staleness question has
  // to be asked here, after the await.
  isStale?: () => boolean;
}

export type OpenWidgetAndSend = (
  text: string,
  ctx: OpenWidgetAndSendContext,
  options?: OpenWidgetAndSendOptions,
) => Promise<OpenWidgetAndSendOutcome>;

function buildPayload(prompt: string, ctx: OpenWidgetAndSendContext): MessagePayload {
  try {
    const payload = buildButtonPromptSelected(ctx);
    if (payload.text.trim()) {
      return payload;
    }
    // The wire text comes from the offered set, so a blank entry there would slip
    // past the no-text guard below.
    logger.warn('[launchers] Sending without attribution: the offered prompt has no text', ctx.selectedPromptId);
  } catch (error) {
    // Only a selection missing from the offered set gets here, which is a
    // renderer bug — log at a level production does not mute.
    logger.error('[launchers] Could not build the prompt attribution', error);
  }
  // Losing the attribution beats losing the customer's message.
  return { text: prompt };
}

// How long one send may hold up the next, and how long a prompt stays claimed. A
// stall is not a failure — nothing rejects — so without this both are held for the
// life of the tab, outliving the launcher and leaving a rebuilt pill inert.
//
// Set beyond a healthy cold path, not at its edge: expiry re-enables the pill and
// invites the re-tap this file exists to absorb, and chunk, config, auth and socket
// take many seconds on poor mobile.
//
// The send is never cancelled, so overrunning costs ordering and risks a duplicate
// if the stalled send lands late, which beats a prompt nobody can ask. Recovers a
// post-load stall only: loadFullWidget coalesces later callers onto one promise, so
// a load that never settles takes every click with it regardless.
export const QUEUE_BUDGET_MS = 30_000;

// A prompt is identified by its launcher as well as its id: two launchers on one
// page mint ids independently, so the id alone is not unique to a pill.
function promptKey(ctx: OpenWidgetAndSendContext): string {
  return `${ctx.launcherId}\u0000${ctx.selectedPromptId}`;
}

export function createOpenWidgetAndSend({
  getActionEnv,
  isWidgetLoaded,
  loadFullWidget,
  trackWidgetOpened,
}: OpenWidgetAndSendDeps): OpenWidgetAndSend {
  // Bridge-scoped, not per-mount: an SPA re-gate destroys the launcher while a
  // send is still in flight, and state living on the mount would go with it.
  let chain: Promise<OpenWidgetAndSendOutcome | void> = Promise.resolve();
  // Holds the run that owns each claim, not just the fact of one: a claim released
  // by its budget can be taken by a later tap, and the first run must not then
  // clear a claim that is no longer its own. The staleness hook rides along so a
  // claim can be told apart from a live one.
  const sending = new Map<string, { run: Promise<OpenWidgetAndSendOutcome>; isStale?: () => boolean }>();

  async function send(
    prompt: string,
    ctx: OpenWidgetAndSendContext,
    isStale?: () => boolean,
  ): Promise<OpenWidgetAndSendOutcome> {
    // Ahead of the load, which destroys the bootstrap analytics before it
    // returns — and so ahead of both drops below, which leave the open on
    // record for a send that never happened.
    const openReported = trackWidgetOpened();
    // Ahead of the chunk load: those seconds are all the handshake gets.
    // Warmed, not opened — the drops below can end this without a panel.
    warmBeaconConnection();
    try {
      return await attemptSend(prompt, ctx, openReported, isStale);
    } catch (error) {
      // A rejection reaches no claim either, and leaves no drop behind it.
      coolBeaconConnection();
      throw error;
    }
  }

  async function attemptSend(
    prompt: string,
    ctx: OpenWidgetAndSendContext,
    openReported: boolean,
    isStale?: () => boolean,
  ): Promise<OpenWidgetAndSendOutcome> {
    if (!isWidgetLoaded()) {
      // Deliberately no initialMessage: the send belongs after the reveal below,
      // and loadFullWidget awaits its own send, which would invert that.
      await loadFullWidget({});
    }

    // The shopper may have navigated while the chunk loaded. Sending now would put
    // the previous page's prompt, and its pageUrl, into the panel.
    if (isStale?.()) {
      logger.log('[launchers] Dropping a prompt whose launcher is gone', ctx.launcherId);
      coolBeaconConnection();
      return OPEN_WIDGET_AND_SEND_OUTCOMES.STALE;
    }

    const env = getActionEnv();
    if (!env) {
      logger.warn('[launchers] Dropping prompt: action environment not ready');
      coolBeaconConnection();
      return OPEN_WIDGET_AND_SEND_OUTCOMES.DROPPED;
    }

    const payload = buildPayload(prompt, ctx);

    // The only rendered echo of an unsent message. Without it the panel reads as
    // empty until authentication completes.
    env.store.messagesMutations.setPendingInitialMessage(payload.text);

    // Ahead of the send: a suppressed or minimized widget would take the message
    // with nothing visible on the page, and a display mode already flipped to
    // Panel by the send would report a Panel-to-Panel transition.
    env.store.uiMutations.showWidgetRequestedByApi();
    if (env.store.ui.displayMode === DisplayMode.Minimized) {
      const { unminimizeWidget } = await import('@/actions/ui/unminimizeWidget');
      // A cold load can rehydrate into Minimized; that reveal is the open
      // already reported above. USER_INTERACTION, not API: a pill is a gesture
      // on the page, so both tiers attribute the same click the same way.
      unminimizeWidget(env, 'USER_INTERACTION', { trackViewChange: !openReported });
    }

    // Not startConversation — it throws on an active session.
    const { authOrCreateMessage } = await import('@/actions/messages/authOrCreateMessage');
    await authOrCreateMessage(env, payload);
    return OPEN_WIDGET_AND_SEND_OUTCOMES.SENT;
  }

  // async, so a throw anywhere below reaches the caller as a rejection. Callers fire
  // this without awaiting it in the same statement, and a synchronous throw would
  // escape the .catch they attach. The body runs to completion before the first
  // await regardless, so the de-duplication below is still decided on the spot.
  return async function openWidgetAndSend(text, ctx, options = {}): Promise<OpenWidgetAndSendOutcome> {
    // Launcher payloads are not validated yet, so blank prompt text is reachable
    // from config. Sending it would create a customer and open the panel for a
    // message that never arrives.
    const prompt = text.trim();
    if (!prompt) {
      logger.warn('[launchers] Ignoring prompt with no text');
      return OPEN_WIDGET_AND_SEND_OUTCOMES.DROPPED;
    }

    // The widget takes a second or two to arrive and the pill does not change while
    // it does, so a repeat tap is what an unsure shopper does — not a second
    // question. A different prompt queues instead, and is sent in turn.
    //
    // Only a live claim absorbs. A re-mounted launcher brings back the same ids, so
    // a claim held by a run whose launcher is already gone would swallow a tap on
    // the new page — one that will send, where the claim it collided with is about
    // to be dropped as stale.
    const key = promptKey(ctx);
    const claim = sending.get(key);
    if (claim && !claim.isStale?.()) {
      logger.log('[launchers] Absorbing a repeat click on a prompt already sending', ctx.selectedPromptId);
      return OPEN_WIDGET_AND_SEND_OUTCOMES.ABSORBED;
    }
    logger.log('[launchers] Opening the widget to send a prompt', {
      launcherId: ctx.launcherId,
      selectedPromptId: ctx.selectedPromptId,
      source: ctx.source,
    });

    let timer: ReturnType<typeof setTimeout> | undefined;
    let spendBudget!: () => void;
    const budget = new Promise<OpenWidgetAndSendOutcome>((resolve) => {
      spendBudget = () => resolve(OPEN_WIDGET_AND_SEND_OUTCOMES.TIMED_OUT);
    });

    const run = chain.then(() => {
      // Armed where the send begins, not where the click was queued: a budget
      // started at enqueue would be spent waiting behind other clicks, and would
      // then expire moments into a send it was supposed to bound.
      timer = setTimeout(() => {
        // Overdue enough to stop being a reason to hold anything back, including
        // its own prompt: releasing the claim is what lets a shopper ask again.
        releaseClaim();
        spendBudget();
      }, QUEUE_BUDGET_MS);
      return send(prompt, ctx, options.isStale);
    });
    sending.set(key, { run, isStale: options.isStale });

    function releaseClaim(): void {
      if (sending.get(key)?.run === run) {
        sending.delete(key);
      }
    }

    // Cleared on the normal path, so a page of ordinary clicks leaves nothing
    // pending behind them.
    void run
      .catch(() => {})
      .finally(() => {
        clearTimeout(timer);
        releaseClaim();
      });

    // The caller is released with the queue rather than held to the send: it holds
    // the promise whose settling frees the pill, and a pill still marked busy after
    // the budget has given up on its send is one the shopper cannot use again.
    const budgeted = Promise.race([run, budget]);
    // The caller still sees its own failure; the queue must not carry it, or one
    // failed click would reject every later one.
    chain = budgeted.catch(() => {});
    return budgeted;
  };
}
